import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, UnstyledButton, Group, Text, Badge, Box, Loader, TextInput } from '@mantine/core';
import { ChevronDown, ChevronRight, ArrowLeft, Search } from 'react-feather';
import { notifications } from '@mantine/notifications';
import {
  usePersonaStore,
  PERSONA_CONFIG,
  type SwitchablePersona,
} from '../stores/persona';
import { useAuthStore } from '../stores/auth';
import { useImpersonationStore } from '../stores/impersonation';
import { authService, type ImpersonatableUser } from '../services/api/auth';
import classes from './PersonaSwitcher.module.css';

// Map frontend persona names to backend persona types for the API
const PERSONA_TO_DB: Record<SwitchablePersona, string> = {
  admin: 'internal_admin',
  cohort_manager: 'internal_user',
  coach: 'coach',
  participant: 'participant',
  partner: 'partner',
};

// Roles available for impersonation (cannot impersonate other admins)
const IMPERSONATABLE_ROLES: SwitchablePersona[] = [
  'cohort_manager',
  'coach',
  'participant',
  'partner',
];

export function PersonaSwitcher() {
  const navigate = useNavigate();
  const { activePersona, isFullAdmin } = usePersonaStore();
  const { user, accessToken, refreshToken, setAuth } = useAuthStore();
  const { isImpersonating, startImpersonation } = useImpersonationStore();
  const { setActivePersona, setIsAdmin, setIsFullAdmin } = usePersonaStore();

  const [menuOpened, setMenuOpened] = useState(false);
  const [selectedRole, setSelectedRole] = useState<SwitchablePersona | null>(null);
  const [users, setUsers] = useState<ImpersonatableUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Only show for full admins (internal_admin role only)
  if (!isFullAdmin) {
    return null;
  }

  // Disable during impersonation
  if (isImpersonating) {
    return (
      <UnstyledButton className={classes.switcher} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
        <Group gap="xs">
          <Badge size="sm" variant="light" color="red" className={classes.badge}>
            Impersonating
          </Badge>
        </Group>
      </UnstyledButton>
    );
  }

  const currentConfig = PERSONA_CONFIG[activePersona];

  const handleRoleSelect = async (role: SwitchablePersona) => {
    setSelectedRole(role);
    setLoadingUsers(true);
    try {
      const dbPersona = PERSONA_TO_DB[role];
      const response = await authService.listUsersByPersona(dbPersona);
      setUsers(response.users);
    } catch {
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSelect = async (targetUser: ImpersonatableUser) => {
    if (!user || !accessToken || !refreshToken) {
      // A failed end of impersonation drops the refresh token (MED-1383). Say so.
      notifications.show({
        title: 'Log in again to impersonate',
        message: 'Your login is missing a part that impersonation needs. Log out, then log in again.',
        color: 'red',
      });
      return;
    }

    // Partner contacts don't have user accounts — use frontend-only view switch
    if (selectedRole === 'partner') {
      startImpersonation({
        mode: 'frontend',
        originalUser: user,
        originalAccessToken: accessToken,
        originalRefreshToken: refreshToken,
        adminName: user.display_name || `${user.first_name} ${user.last_name}`,
        adminEmail: user.email,
        impersonatedUserName: [targetUser.first_name, targetUser.last_name].filter(Boolean).join(' ') || targetUser.email || 'Unknown user',
        impersonatedUserRole: PERSONA_CONFIG.partner.label,
      });

      setActivePersona('partner');
      setIsAdmin(false);
      setIsFullAdmin(false);

      setMenuOpened(false);
      setSelectedRole(null);
      setUsers([]);
      setSearchQuery('');

      // Defer navigation so all store updates flush before route guard evaluates
      setTimeout(() => navigate(PERSONA_CONFIG.partner.defaultRoute), 0);
      return;
    }

    try {
      const response = await authService.impersonate(targetUser.id);

      // Save original admin state before switching
      startImpersonation({
        mode: 'backend',
        originalUser: user,
        originalAccessToken: accessToken,
        originalRefreshToken: refreshToken,
        adminName: response.impersonation.adminName,
        adminEmail: response.impersonation.adminEmail,
        impersonatedUserName: [targetUser.first_name, targetUser.last_name].filter(Boolean).join(' ') || targetUser.email || 'Unknown user',
        impersonatedUserRole: selectedRole ? PERSONA_CONFIG[selectedRole].label : 'User',
      });

      // Switch auth to the impersonated user
      setAuth(response.user, response.tokens.accessToken, response.tokens.refreshToken);

      // Set persona to match impersonated user's role
      if (selectedRole) {
        setActivePersona(selectedRole);

        // Update admin flags since we're now viewing as a non-admin
        if (selectedRole !== 'admin') {
          if (selectedRole === 'cohort_manager') {
            setIsAdmin(true);
            setIsFullAdmin(false);
          } else {
            setIsAdmin(false);
            setIsFullAdmin(false);
          }
        }
      }

      // Close menu and reset state
      setMenuOpened(false);
      setSelectedRole(null);
      setUsers([]);
      setSearchQuery('');

      // Defer navigation so all store updates flush before route guard evaluates
      if (selectedRole) {
        setTimeout(() => navigate(PERSONA_CONFIG[selectedRole].defaultRoute), 0);
      }
    } catch (error) {
      console.error('Failed to start impersonation:', error);
    }
  };

  const handleBack = () => {
    setSelectedRole(null);
    setUsers([]);
    setSearchQuery('');
  };

  const handleMenuClose = () => {
    setMenuOpened(false);
    setSelectedRole(null);
    setUsers([]);
    setSearchQuery('');
  };

  const filteredUsers = searchQuery
    ? users.filter(u => {
        const q = searchQuery.toLowerCase();
        const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ').toLowerCase();
        return (
          u.first_name?.toLowerCase().includes(q) ||
          u.last_name?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          (fullName !== '' && fullName.includes(q))
        );
      })
    : users;

  return (
    <Menu
      shadow="md"
      width={260}
      position="bottom-end"
      opened={menuOpened}
      closeOnItemClick={false}
      onChange={(opened) => {
        if (opened) {
          setMenuOpened(true);
        } else {
          handleMenuClose();
        }
      }}
    >
      <Menu.Target>
        <UnstyledButton className={classes.switcher}>
          <Group gap="xs">
            <Badge
              size="sm"
              variant="light"
              color={currentConfig.color}
              className={classes.badge}
            >
              {currentConfig.shortLabel}
            </Badge>
            <ChevronDown size={14} className={classes.chevron} />
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        {!selectedRole ? (
          <>
            <Menu.Label>Impersonate as Role</Menu.Label>
            {IMPERSONATABLE_ROLES.map(personaId => {
              const config = PERSONA_CONFIG[personaId];
              return (
                <Menu.Item
                  key={personaId}
                  onClick={() => handleRoleSelect(personaId)}
                  rightSection={<ChevronRight size={14} />}
                >
                  <Group gap="sm">
                    <Badge size="xs" variant="dot" color={config.color}>
                      {config.label}
                    </Badge>
                  </Group>
                </Menu.Item>
              );
            })}
            <Menu.Divider />
            <Box px="sm" py="xs">
              <Text size="xs" c="dimmed">
                Viewing as {currentConfig.label}
              </Text>
            </Box>
          </>
        ) : (
          <>
            <Menu.Item onClick={handleBack} leftSection={<ArrowLeft size={14} />}>
              <Text size="sm" fw={500}>
                {PERSONA_CONFIG[selectedRole].label === 'Coach' ? 'Coaches' : `${PERSONA_CONFIG[selectedRole].label}s`}
              </Text>
            </Menu.Item>
            <Menu.Divider />
            {loadingUsers ? (
              <Box py="md" ta="center">
                <Loader size="sm" />
              </Box>
            ) : users.length === 0 ? (
              <Box px="sm" py="md">
                <Text size="xs" c="dimmed" ta="center">
                  No users found with this role
                </Text>
              </Box>
            ) : (
              <>
                <Box px="xs" py={4}>
                  <TextInput
                    placeholder="Search users..."
                    size="xs"
                    leftSection={<Search size={12} />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.currentTarget.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </Box>
                {filteredUsers.length === 0 ? (
                  <Box px="sm" py="md">
                    <Text size="xs" c="dimmed" ta="center">
                      No matching users
                    </Text>
                  </Box>
                ) : (
                  <Box style={{ maxHeight: 300, overflowY: 'auto' }}>
                    {filteredUsers.map(u => (
                      <Menu.Item
                        key={u.id}
                        onClick={() => handleUserSelect(u)}
                      >
                        <Box>
                          <Text size="sm" fw={500}>
                            {u.first_name} {u.last_name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {u.email}
                          </Text>
                        </Box>
                      </Menu.Item>
                    ))}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

export default PersonaSwitcher;
